import { AVAILABILITY_TYPES, DELIVERY_TYPES } from "../config/availability";

const isMobileType = (availabilityType) =>
  availabilityType === AVAILABILITY_TYPES.DISTANCE ||
  availabilityType === AVAILABILITY_TYPES.REGION;

const isFixedType = (availabilityType) =>
  availabilityType === AVAILABILITY_TYPES.FIXED;

// "Most recent" = most-recently-created. The `["availabilities"]` query
// (GET /availability) strips `createdAt`/`updatedAt` from the payload, so a
// timestamp sort is impossible here — but every record still carries `_id`,
// and a MongoDB ObjectId's leading 4 bytes ARE its creation time. Comparing
// the id strings lexicographically therefore recovers creation order without
// the missing timestamps; descending puts the newest first.
const mostRecent = (availabilities) => {
  if (availabilities.length === 0) return null;
  const sorted = [...availabilities].sort((a, b) =>
    String(b._id).localeCompare(String(a._id))
  );
  return { id: sorted[0]._id, name: sorted[0].name };
};

/**
 * Derives what availability the provider currently offers, from the
 * `["availabilities"]` query result.
 *
 * `inactiveMobile` / `inactiveFixed` identify the most recent *disabled*
 * availability of a type that has no active counterpart, and
 * `inactiveMobileCount` / `inactiveFixedCount` how many such disabled records
 * exist. `inactiveMobileList` / `inactiveFixedList` are the FULL raw disabled
 * records (carrying `_id`, `name`, `availabilityType`, `isActive`) so the UI
 * can render a toggle-list of every one — used by `ReenableAvailabilityModal`.
 * All of these are only populated when the provider has NO active
 * availability of that type; counts/lists are empty once an active one exists.
 *
 * @param {Array} availabilities
 * @returns {{ providerHasMobile: boolean, providerHasFixed: boolean,
 *             inactiveMobile: {id: string, name: string}|null,
 *             inactiveFixed: {id: string, name: string}|null,
 *             inactiveMobileCount: number, inactiveFixedCount: number,
 *             inactiveMobileList: Array, inactiveFixedList: Array }}
 */
export const deriveProviderAvailabilityTypes = (availabilities = []) => {
  const providerHasMobile = availabilities.some(
    (a) => a.isActive !== false && isMobileType(a.availabilityType)
  );
  const providerHasFixed = availabilities.some(
    (a) => a.isActive !== false && isFixedType(a.availabilityType)
  );

  const inactiveMobileList = providerHasMobile
    ? []
    : availabilities.filter(
        (a) => a.isActive === false && isMobileType(a.availabilityType)
      );
  const inactiveFixedList = providerHasFixed
    ? []
    : availabilities.filter(
        (a) => a.isActive === false && isFixedType(a.availabilityType)
      );

  return {
    providerHasMobile,
    providerHasFixed,
    inactiveMobile: mostRecent(inactiveMobileList),
    inactiveFixed: mostRecent(inactiveFixedList),
    inactiveMobileCount: inactiveMobileList.length,
    inactiveFixedCount: inactiveFixedList.length,
    inactiveMobileList,
    inactiveFixedList,
  };
};

/**
 * The delivery type a NEW service should default to, inferred from the
 * provider's active availability. Exactly one active type → that type;
 * both or neither → hybrid (safe either-way fallback).
 */
export const inferDeliveryType = (providerHasMobile, providerHasFixed) => {
  if (providerHasMobile && !providerHasFixed) return DELIVERY_TYPES.MOBILE;
  if (providerHasFixed && !providerHasMobile) return DELIVERY_TYPES.FIXED;
  return DELIVERY_TYPES.HYBRID; // both or neither
};

/**
 * Whether a service can currently take bookings given the provider's active
 * availability types. Mirrors the server-side public listing filter: mobile
 * needs a mobile availability, fixed needs a fixed one, hybrid needs either.
 *
 * A hybrid service with exactly one availability type is bookable (in that
 * mode) and gets no attention treatment.
 *
 * @param {{ deliveryType?: string }} service
 * @param {{ providerHasMobile: boolean, providerHasFixed: boolean }} providerTypes
 * @returns {{ bookable: boolean, missing: "mobile"|"fixed"|"any"|null }}
 */
/**
 * Page-level derivation shared by ServicePage and DashboardPage: which of the
 * provider's services can't currently take bookings.
 *
 * Services the provider deliberately disabled (`isActive === false`) are
 * skipped entirely — they get no bookability entry (so no attention badge)
 * and don't count toward the strip. Their gray "Unavailable" status already
 * tells that story; the amber signal is reserved for services the provider
 * expects to be bookable.
 *
 * @param {Array} services - the `["services"]` query result
 * @param {Array} availabilities - the `["availabilities"]` query result
 * @returns {{ bookabilityById: Object, unbookable: Array, providerTypes: Object }}
 */
export const deriveUnbookableServices = (services = [], availabilities = []) => {
  const providerTypes = deriveProviderAvailabilityTypes(availabilities);
  const bookabilityById = {};
  const unbookable = [];

  for (const service of services) {
    if (service.isActive === false) continue;
    const bookability = getServiceBookability(service, providerTypes);
    bookabilityById[service._id] = bookability;
    if (!bookability.bookable) unbookable.push(service);
  }

  return { bookabilityById, unbookable, providerTypes };
};

export const getServiceBookability = (service, { providerHasMobile, providerHasFixed }) => {
  switch (service.deliveryType) {
    case DELIVERY_TYPES.MOBILE:
      return providerHasMobile
        ? { bookable: true, missing: null }
        : { bookable: false, missing: "mobile" };
    case DELIVERY_TYPES.FIXED:
      return providerHasFixed
        ? { bookable: true, missing: null }
        : { bookable: false, missing: "fixed" };
    default:
      // hybrid (or legacy services without the field)
      return providerHasMobile || providerHasFixed
        ? { bookable: true, missing: null }
        : { bookable: false, missing: "any" };
  }
};
